/**
 * 수업기록·평가기록·행동특성 AI 피드백 — 클라이언트 측 익명화 + 요청.
 *
 * 개인정보 처리:
 * - 요청마다 학생별 무작위 코드(S + 영문/숫자 3자, 예: S7KQ)를 새로 만든다. 대응표는 이 브라우저 메모리에만 있고
 *   서버로 보내지도, 저장하지도 않는다.
 * - 메모의 멤버 태그(#m:id, #m:id-, #이름)는 [코드] / [코드 제외] 로, #전체 는 [전체 학생] 으로,
 *   봉인 태그(#e:…)와 해석되지 않는 태그는 [태그] 로 바꾼다.
 * - 태그가 아닌 평문에 나온 학급 멤버 이름(전체 이름·표시 이름·성을 뺀 이름)과 입장번호도 [코드] 로 바꾼다.
 *   (수업 정보 문자열에도 같은 처리를 한다)
 */
import { supabase } from '../supabaseClient';
import { aiAccessErrorMessage, syncAiQuotaFromResponse } from '../services/aiQuotaService';
import type { ClassMemberRow } from './classMembersStorage';
import { displayClassMemberName } from './classMemberNameDisplay';
import {
  isSealedTagBody,
  parseMemberIdFromTagBody,
  resolveMemberTagUnique,
} from './classRecordMemoHashtags';

export type AiRecordFeedbackKind = 'lesson' | 'eval_log' | 'behavior';

export type AiRecordFeedbackStudent = {
  code: string;
  analysis: string;
  guidance: string;
};

export type AiRecordFeedbackResult = {
  overall: string;
  students: AiRecordFeedbackStudent[];
};

export type AnonymizedRecord = {
  note: string;
  context: string;
  /** 기록에서 개별 언급된 학생 코드 */
  codes: string[];
  /** 코드 → 멤버 (클라이언트 메모리 전용) */
  codeToMember: Record<string, ClassMemberRow>;
};

const TAG_RE = /#[^\s#]+/g;
const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const AI_FEEDBACK_MAX_CHARS = 500;

function randomCode(used: Set<string>): string {
  const buf = new Uint32Array(3);
  for (let attempt = 0; attempt < 50; attempt += 1) {
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(buf);
    else for (let i = 0; i < 3; i += 1) buf[i] = Math.floor(Math.random() * 1e9);
    const code = 'S' + Array.from(buf, (n) => CODE_ALPHABET[n % CODE_ALPHABET.length]).join('');
    if (!used.has(code)) {
      used.add(code);
      return code;
    }
  }
  const fallback = `S${used.size + 100}`;
  used.add(fallback);
  return fallback;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** 평문에서 지울 이름 후보(긴 것부터 치환) */
function nameVariants(m: ClassMemberRow): string[] {
  const out = new Set<string>();
  const full = (m.name ?? '').trim();
  const shown = displayClassMemberName(m).trim();
  if (full.length >= 2) out.add(full);
  if (shown.length >= 2) out.add(shown);
  const chars = Array.from(full);
  // 한글 3~4글자 이름은 성을 뺀 이름(예: 민준)도 지운다
  if (/^[가-힣]{3,4}$/.test(full)) out.add(chars.slice(1).join(''));
  return Array.from(out);
}

export function anonymizeRecord(params: {
  note: string;
  contextLines: Array<string | null | undefined>;
  /** 태그 해석·칩 표시 대상(그룹 멤버) */
  members: ClassMemberRow[];
  /** 평문 이름 제거 대상(학급 전체 멤버). 없으면 members */
  allMembers?: ClassMemberRow[];
}): AnonymizedRecord {
  const pool = new Map<string, ClassMemberRow>();
  [...(params.allMembers ?? []), ...params.members].forEach((m) => {
    if (m?.id) pool.set(m.id, m);
  });
  const poolList = Array.from(pool.values());

  const used = new Set<string>();
  const codeById = new Map<string, string>();
  const codeToMember: Record<string, ClassMemberRow> = {};
  const referenced: string[] = [];
  const codeFor = (m: ClassMemberRow, reference: boolean): string => {
    let code = codeById.get(m.id);
    if (!code) {
      code = randomCode(used);
      codeById.set(m.id, code);
      codeToMember[code] = m;
    }
    if (reference && !referenced.includes(code)) referenced.push(code);
    return code;
  };

  const replaceTags = (text: string): string =>
    text.replace(TAG_RE, (raw) => {
      const body = raw.slice(1);
      if (body === '전체') return '[전체 학생]';
      if (isSealedTagBody(body)) return '[태그]';
      const parsed = parseMemberIdFromTagBody(body);
      if (parsed) {
        const hit = pool.get(parsed.memberId);
        if (!hit) return '[학생]';
        const code = codeFor(hit, true);
        return parsed.exclude ? `[${code} 제외]` : `[${code}]`;
      }
      if (body.endsWith('-') && body.length > 1) {
        const hit = resolveMemberTagUnique(params.members, body.slice(0, -1));
        return hit ? `[${codeFor(hit, true)} 제외]` : '[학생]';
      }
      const hit = resolveMemberTagUnique(params.members, body);
      return hit ? `[${codeFor(hit, true)}]` : '[학생]';
    });

  // 평문 이름은 개인정보 보호를 위해 항상 지우되, 학생 코드로 연결하는 것은
  // 이 기록에 태그(정확한 멤버 id)로 등장한 학생뿐이다. 동명(예: 성 뺀 '은준')이 여럿이면
  // 태그된 학생 중 유일할 때만 그 학생 코드로, 아니면 [학생] 으로 바꾼다(다른 반 학생으로 오매핑 방지).
  const tagged = new Set<string>();
  // 1차: 태그만 해석해 태그된 학생 id 수집
  replaceTags(params.note ?? '');
  Object.values(codeToMember).forEach((m) => tagged.add(m.id));
  const byVariant = new Map<string, ClassMemberRow[]>();
  poolList.forEach((m) =>
    nameVariants(m).forEach((text) => {
      const arr = byVariant.get(text) ?? [];
      if (!arr.some((x) => x.id === m.id)) arr.push(m);
      byVariant.set(text, arr);
    })
  );
  const variantTexts = Array.from(byVariant.keys()).sort((x, y) => y.length - x.length);
  const entryNumbers = poolList
    .map((m) => ({ n: (m.entryNumber ?? '').trim(), m }))
    .filter((x) => /^\d{4,6}$/.test(x.n) && !/^0+$/.test(x.n));

  const replacePlainNames = (text: string, _reference: boolean): string => {
    let s = text;
    variantTexts.forEach((name) => {
      if (!s.includes(name)) return;
      const taggedHits = (byVariant.get(name) ?? []).filter((m) => tagged.has(m.id));
      const label = taggedHits.length === 1 ? `[${codeFor(taggedHits[0], false)}]` : '[학생]';
      s = s.replace(new RegExp(escapeRegExp(name), 'g'), label);
    });
    entryNumbers.forEach(({ n, m }) => {
      const label = tagged.has(m.id) ? `[${codeFor(m, false)}]` : '[학생]';
      s = s.replace(new RegExp(`(^|\\D)${n}(?!\\d)`, 'g'), (_all, pre: string) => `${pre}${label}`);
    });
    return s;
  };

  const note = replacePlainNames(replaceTags(params.note ?? ''), true)
    .replace(/\u200b/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .trim();
  const context = params.contextLines
    .map((l) => (l ?? '').trim())
    .filter(Boolean)
    .map((l) => replacePlainNames(replaceTags(l), false))
    .join('\n');

  return { note, context, codes: referenced, codeToMember };
}

/** 문자 수 기준 방어적 상한(말줄임 없이 자름) */
export function clipFeedbackText(s: string, max = AI_FEEDBACK_MAX_CHARS): string {
  const arr = Array.from((s ?? '').trim());
  return arr.length <= max ? arr.join('') : arr.slice(0, max).join('').trim();
}

const ERROR_MESSAGES: Record<string, string> = {
  AI_NOT_CONFIGURED: 'AI 키가 설정되지 않았습니다. (서버 env ANTHROPIC_API_KEY)',
  UNAUTHORIZED: '로그인이 필요합니다. 다시 로그인해 주세요.',
  RATE_LIMITED: '요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.',
  PROMPT_TOO_LONG: '기록이 너무 깁니다. 내용을 줄여서 다시 시도해 주세요.',
  EMPTY_NOTE: '먼저 기록 내용을 입력해 주세요.',
  FORBIDDEN: '현재 이용권에서는 AI 피드백을 사용할 수 없습니다.',
  INVALID_CODES: '요청 형식이 올바르지 않습니다.',
};

function apiUrl(): string {
  const base = (process.env.REACT_APP_API_BASE as string | undefined)?.replace(/\/$/, '') ?? '';
  return base ? `${base}/api/ai-record-feedback` : '/api/ai-record-feedback';
}

export async function requestAiRecordFeedback(
  kind: AiRecordFeedbackKind,
  anon: AnonymizedRecord,
  signal?: AbortSignal
): Promise<AiRecordFeedbackResult> {
  if (!anon.note.trim()) throw new Error(ERROR_MESSAGES.EMPTY_NOTE);
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error(ERROR_MESSAGES.UNAUTHORIZED);
  let res: Response;
  try {
    res = await fetch(apiUrl(), {
      method: 'POST',
      signal,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ kind, note: anon.note, context: anon.context, codes: anon.codes }),
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new Error('AI 서버에 연결하지 못했습니다.');
  }
  const json = (await res.json().catch(() => null)) as
    | { ok: true; overall?: string; students?: AiRecordFeedbackStudent[] }
    | { ok: false; error?: string }
    | null;
  syncAiQuotaFromResponse(json);
  if (!json || !json.ok) {
    const code = (json && !json.ok && json.error) || `HTTP_${res.status}`;
    throw new Error(aiAccessErrorMessage(code, json) ?? ERROR_MESSAGES[code] ?? `AI 피드백을 받지 못했습니다. (${code})`);
  }
  const allowed = new Set(anon.codes);
  return {
    overall: clipFeedbackText(json.overall ?? ''),
    students: (json.students ?? [])
      .filter((s) => s && allowed.has(s.code))
      .map((s) => ({
        code: s.code,
        analysis: clipFeedbackText(s.analysis ?? ''),
        guidance: clipFeedbackText(s.guidance ?? ''),
      })),
  };
}
